import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Card } from '@/components/ui/card';
import { useSeo } from '@/lib/seo';

const PORTRAIT =
  'https://images.unsplash.com/photo-1507126117511-e87526de90e2?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85';

export default function AboutPage() {
  useSeo({
    title: 'About | A Guy Who Hunts',
    description:
      'A self-taught adult hunter documenting the real process of trying to become a better whitetail hunter on pressured public land and 1.25 acres behind the house.',
  });

  return (
    <div>
      <section className="relative border-b border-[hsl(var(--border))] hero-radial">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid gap-10 lg:grid-cols-12 items-center">
          <div className="lg:col-span-6">
            <p className="kicker">About</p>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2 leading-tight">
              I did not grow up hunting.
            </h1>
            <p className="mt-4 text-[hsl(var(--muted-foreground))] max-w-xl leading-relaxed">
              I started as an adult and have mostly learned through research, observation, trial, error, and time in the
              woods.
            </p>
          </div>
          <div className="lg:col-span-6">
            <Card className="overflow-hidden border-[hsl(var(--border))] card-elev">
              <AspectRatio ratio={4 / 5}>
                <img
                  src={PORTRAIT}
                  alt="Documentary-style outdoor portrait — placeholder image."
                  className="h-full w-full object-cover"
                />
              </AspectRatio>
            </Card>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[72ch] px-4 sm:px-6 lg:px-8 py-14 lg:py-18 prose-editorial">
        <p>
          I am not building this because I have everything figured out. I am building it because I do not.
        </p>
        <p>
          <strong>A Guy Who Hunts</strong> documents the real process of trying to become a better hunter while
          balancing work, family, limited time, pressured public land, and the small pieces of ground I can actually
          access.
        </p>
        <p>
          One of those places is only about 1.25 acres behind my home. Last season, I passed a legal buck there and
          later came close to an opportunity on a larger deer. I still have not harvested my first whitetail.
        </p>
        <blockquote>
          This is the story of trying to change that — without pretending every sit is successful or every decision is
          smart.
        </blockquote>

        <h2>What this site is not</h2>
        <ul>
          <li>Not a gear-review publication.</li>
          <li>Not an outfitter or booking service.</li>
          <li>Not an expert instruction platform.</li>
        </ul>

        <h2>What it is</h2>
        <p>
          An honest, documentary-style archive of one season at a time — videos, field notes, trail-camera updates,
          scouting observations, mistakes, and whatever the outcome turns out to be.
        </p>
      </section>

      <section className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 pb-20 text-center">
        <p className="kicker">Tagline</p>
        <p className="font-serif text-3xl sm:text-4xl mt-2 tracking-tight">
          Public land. Limited time. <span className="italic">Honest results.</span>
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/season">
            <Button size="lg">Follow the Season</Button>
          </Link>
          <Link to="/newsletter">
            <Button size="lg" variant="secondary">Subscribe</Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
